import {useMemo, useCallback, useState, useEffect} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";

import {canEditTeamIds, listFieldCustomizations, listFields} from "../Api";
import {IdeaPriority} from "../types/types";
import {useKnowledgeData} from "./useKnowledgeData";
import _ from "lodash";
import {addOptionsToC360Filters} from "../context/helpers";
import {fetchDomainToggle} from "../api/domains";
import {
  FieldCustomizations,
  Fields,
  handleFlagsData,
  initialFieldCustomizations,
  initialFields,
  processFields, sortData
} from "./helpers/fieldsHelpers";
import { useIdeaPages } from "./useIdeaPages";

interface UseFieldsReturn extends Fields, FieldCustomizations {
  fieldsLoaded: boolean;
  editCustomizations: any | undefined;
  getFields: any;
  showDomain: boolean | undefined;
  subPages: any[];
  defaultRequestPriority: any;
  markedDeliveryStatus: any[];
  setAllComponents: (components: any[]) => void;
  setFields: React.Dispatch<React.SetStateAction<any>>;
  refreshSubPages: (auth0: any) => Promise<unknown>;
  // Cache management
  clearCache: () => void;
  invalidateFilters: () => void;
  getCacheKey: () => readonly any[];
}

// Query keys factory
const fieldsKeys = {
  all: [] as const,
  config: () => [...fieldsKeys.all, "config"] as const,
  fieldCustomizations: () => [...fieldsKeys.all, "fieldCustomizations"] as const,
  editCustomizations: () => [...fieldsKeys.all, "editCustomizations"] as const,
  showDomain: () => [...fieldsKeys.all, "showDomain"] as const,
  subPages: () => [...fieldsKeys.all, "subPages"] as const,
  // Helper to invalidate all filter config data
  invalidateAll: () => [...fieldsKeys.all] as const,
};

export const useFields = (): UseFieldsReturn => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  const {
    tagsOptions,
    members,
    owners,
    devTeams,
  } = useKnowledgeData();

  const [fields, setFields] = useState<any>([]);
  const [allComponents, setAllComponents] = useState<any[]>([]);
  const [c360FiltersConfig, setC360FiltersConfig] = useState<any>({});
  const [markedDeliveryStatus, setMarkedDeliveryStatus] = useState<any[]>([]);
  const filterConfigQueryKey = useMemo(() => {
    return fieldsKeys.config();
  }, []);

  const baseUseQueryConfig = {
    enabled: !!auth0.isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache for 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false
  };

  // Fields query
  const {
    data: fieldsData,
    isSuccess: fieldsLoaded,
    refetch: refetchFields
  } = useQuery<Fields>({
    ...baseUseQueryConfig,
    queryKey: filterConfigQueryKey,
    placeholderData: initialFields,
    queryFn: async (): Promise<Fields> => {
      const response = await listFields(auth0);
      const ideaColumns = _.get(response, 'ideaColumns') || [];

      return {
        addedAttributes: _.get(response, 'addedAttributes') || [],
        allAggregationProperties: _.get(response, 'allAggregationProperties') || [],
        allColumns: ideaColumns,
        fields: response,
        generatedEnabled: _.get(response, 'generatedEnabled') || false,
        ideaColumns: _.filter(ideaColumns, 'enabled'),
        ideaDefaultSortColumn: _.get(response, 'defaultSortColumn') || null,
        ideaInfoTabsConfig: _.get(response, 'ideaInfoTabsConfig') || {},
        impactAggregationProperties: _.get(response, 'impactAggregationProperties') || [],
        mainPageName: _.get(response, 'mainPageName') || 'Main',
        perspectiveConfigurations: _.get(response, 'perspectiveConfigurations') || [],
        showWhatsNewBanner: _.get(response, 'showWhatsNewBanner') || null
      }
    },
    gcTime: 5 * 60 * 1000, // 5 minutes - keep in cache for 5 minutes
  });

  // Field customizations query
  const {
    data: fieldCustomizationsData,
    isSuccess: fieldCustomizationsLoaded,
    refetch: refetchFieldCustomizations
  } = useQuery<FieldCustomizations>({
    ...baseUseQueryConfig,
    queryKey: fieldsKeys.fieldCustomizations(),
    placeholderData: initialFieldCustomizations,
    queryFn: async (): Promise<FieldCustomizations> => {
      const response = await listFieldCustomizations(auth0);
      const requestPriorities = response.requestPriorities || [];
      const threeSixtyConfiguration = response?.threeSixtyConfiguration || {};
      const threeSixtyConfigurationColumns = _.get(threeSixtyConfiguration, 'colomns') || [];

      const { enableEditOnImportedIdeas, enableIdeaValidation} = handleFlagsData(_.get(response, 'flags'));

      return {
        account: response?.accountDoc,
        accountAdditionalInfo: processFields(_.get(response, 'customizations.account.fields') || null),
        allComponents: _.get(response, 'allComponents') || [],
        businessCategoryMapping: _.get(response, 'businessCategoryMapping[0].businessNames') || [],
        c360FiltersConfig: _.get(response, 'c360FiltersConfig') || {},
        components: _.get(response, 'components') || [],
        customEvidenceObj: _.get(response, 'customizations.customEvidenceObj') || {},
        markedDeliveryStatus: response?.deliveryStatus || [],
        enableEditOnImportedIdeas,
        enableIdeaValidation: _.get(response, 'enableIdeaValidation') || enableIdeaValidation,
        overwriteIdeaStatusWithLinkedDelivery: response?.overwriteIdeaStatusWithLinkedDelivery ?? false,
        gtmAppsConfiguration: _.get(response, 'gtmAppsConfiguration') || null,
        ideaPriorities: sortData(response?.ideaPriorities) || IdeaPriority,
        ideaStatuses: sortData(response?.ideaStatuses) || [],
        objectCustomizations: response?.customizations || {},
        opportunityAdditionalInfo: processFields(_.get(response, 'customizations.opportunity.fields') || null),
        priorityData: _.map(requestPriorities, 'label'),
        quarterConfig: response?.quarterConfig || null,
        requestPriorities: sortData(requestPriorities),
        threeSixtyColumns: threeSixtyConfigurationColumns.sort((a: any, b: any) => (a?.index || 0) - (b?.index || 0)),
        threeSixtyConfig: threeSixtyConfiguration,
        threeSixtyEnabled: threeSixtyConfiguration?.threeSixtyEnabled
      };
    },
    gcTime: 5 * 60 * 1000, // 5 minutes - keep in cache for 5 minutes
  });

  // EditCustomizations query
  const {
    data: editCustomizations,
  } = useQuery({
    ...baseUseQueryConfig,
    queryKey: fieldsKeys.editCustomizations(),
    queryFn: async () => {
      const response = await canEditTeamIds(auth0);
      return response.reduce((acc: any, customization: any) => {
        return {
          ...acc,
          [customization.name]: customization.value,
        };
      }, {})
    }
  });

  // showDomain query
  const {
    data: showDomain,
  } = useQuery({
    ...baseUseQueryConfig,
    queryKey: fieldsKeys.showDomain(),
    queryFn: async () => {
      const response = await fetchDomainToggle(auth0);
      return response.toggle?.length > 0 ? response.toggle[0].useDomains : false;
    }
  });

  const { pages: subPages, refetch: refetchPages, invalidatePages } = useIdeaPages();

  const refreshSubPages = useCallback(
    (_auth0: any) => {
      invalidatePages();
      return refetchPages();
    },
    [refetchPages, invalidatePages]
  );

  const defaultRequestPriority = useMemo(() => {
    const defaultRequest = _.find(fieldCustomizationsData?.requestPriorities, 'isDefault');
    return _.get(defaultRequest, 'label');
  }, [fieldCustomizationsData?.requestPriorities]);

  // Cache management callbacks
  const clearCache = useCallback(() => {
    queryClient.removeQueries({ queryKey: fieldsKeys.all });
    console.log('🗑️ Fields cache cleared');
  }, [queryClient]);

  const invalidateFilters = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: fieldsKeys.invalidateAll() });
    console.log('🔄 Fields cache invalidated for filter changes');
  }, [queryClient]);

  const getCacheKey = useCallback(() => {
    return filterConfigQueryKey;
  }, [filterConfigQueryKey]);

  useEffect(() => {
    if (fieldsData) {
      setFields(fieldsData.fields);
    }
  }, [fieldsData]);

  useEffect(() => {
    if (fieldCustomizationsData) {
      setAllComponents(fieldCustomizationsData.allComponents || []);
      setMarkedDeliveryStatus(fieldCustomizationsData.markedDeliveryStatus || []);
      setC360FiltersConfig(fieldCustomizationsData.c360FiltersConfig || {});
    }
  }, [fieldCustomizationsData]);

  useEffect(() => {
    const baseConfig = fieldCustomizationsData?.c360FiltersConfig || {};
    
    if (baseConfig && Object.keys(baseConfig).length > 0) {
      const updatedC360Filters = addOptionsToC360Filters(baseConfig,
        {
          members,
          owners,
          components: fieldCustomizationsData?.components,
          tagsOptions,
          devTeams
        });
      setC360FiltersConfig(updatedC360Filters);
    }
  }, [
    fieldCustomizationsData?.c360FiltersConfig,
    fieldCustomizationsData?.components,
    members,
    owners,
    devTeams,
    tagsOptions
  ]);

  const getFields = async () => {
    await refetchFields();
    await refetchFieldCustomizations();
  }

  return {
    ...(fieldsData || initialFields),
    ...(_.omit(fieldCustomizationsData || initialFieldCustomizations, ['c360FiltersConfig', 'allComponents'])),
    fieldsLoaded: fieldsLoaded && fieldCustomizationsLoaded,
    fields,
    setFields,
    getFields,
    allComponents,
    setAllComponents,
    c360FiltersConfig,
    defaultRequestPriority,
    showDomain: showDomain ?? false,
    subPages,
    refreshSubPages,
    editCustomizations,
    markedDeliveryStatus,
    // Cache management
    clearCache,
    invalidateFilters,
    getCacheKey,
  };
}; 